import { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { logger, useAuth } from "../core";
import { toastService } from "@/core/services/toast.service";
import { useAppDispatch } from "@/store/hooks";
import {
  clearUser,
  updateUserRole,
  setSessionVersion,
  setSessionType,
} from "@/store/slices/auth";
import type { IUser, SessionType } from "@/store/slices/auth";
import { useSessionQuery } from "@/features/auth/hooks/useSessionQuery";

/**
 * The REAL runtime shape of GET /check-session, after httpService's
 * response.data.data unwrap: { valid: true, data: ISessionData }.
 *
 * authApi.getSession is typed `Promise<IUser>` — that's wrong, always has
 * been; it's the session blob, not a user. Typed narrowly here (just the
 * fields this component needs) rather than fixing that broader
 * declaration, which other callers may depend on as-is.
 */
interface ICheckSessionResponse {
  valid: boolean;
  data?: {
    role?: IUser["role"];
    sessionVersion?: number;
    sessionType?: SessionType;
  };
}

const ProtectedRoutes = () => {
  const dispatch = useAppDispatch();
  const {
    // isLoading,
    error,
    isError,
    isSuccess,
    data: sessionResponse,
  } = useAuth();
  logger.warn("data from get session ===> useAuth() ", sessionResponse);

  /**
   * The 15-second sessionVersion/sessionType poll. Mounted HERE — inside
   * ProtectedRoutes, not globally at the app root (see SessionWatcher.tsx,
   * which caused a /login reload loop when mounted app-wide) — so it only
   * ever runs for an authenticated route subtree and never touches the
   * public /login page at all.
   *
   * This is what makes ElanPay.tsx's selectIsInvoiceStale actually fire: a
   * user who logged in "normal" and then follows a referral/promo link
   * (in this tab or another) gets a sessionType/sessionVersion change on
   * the backend; without this poll updating auth.sessionVersion, the BTC
   * page never learns its on-screen invoice is for a dead price and never
   * re-mints.
   */
  useSessionQuery();

  /**
   * Bridges role AND sessionVersion/sessionType into Redux on every
   * successful check — both the initial mount AND useAuth's
   * refetchOnWindowFocus refetch (see core/hooks/useAuth.ts). Two things
   * this feeds, both without a manual refresh or waiting on the 15s poll:
   *
   *   - role -> selectHasPaid (widened to also check role) -> RequireUnpaid's
   *     existing guard redirects a now-paid tab to /courses on its own.
   *   - sessionVersion -> selectIsInvoiceStale (invoiceSessionSlice.ts) ->
   *     ElanPay.tsx's stale effect drops a dead invoice and mints a fresh
   *     one — a REAL new QR code/bolt11/address, not just a price label —
   *     the moment you refocus a tab that's been sitting on the BTC page
   *     since before a relogin changed the session's price elsewhere.
   *
   * Effect, not inline during render: dispatching during render is a React
   * violation even though this component returns before commit sometimes.
   */
  useEffect(() => {
    if (!isSuccess) return;
    const data = (sessionResponse as ICheckSessionResponse | undefined)?.data;
    if (!data) return;
    if (data.role) dispatch(updateUserRole(data.role));
    if (typeof data.sessionVersion === "number")
      dispatch(setSessionVersion(data.sessionVersion));
    if (data.sessionType) dispatch(setSessionType(data.sessionType));
  }, [isSuccess, sessionResponse, dispatch]);

  if (isError) {
    toastService.error("Not Found", error?.message as string);
    dispatch(clearUser());
    return <Navigate to="/login" />;
  }
  if (isSuccess) {
    return <Outlet />;
  }
};
export default ProtectedRoutes;

/**
 * Layer 1 of the route protection: "is there a session at all?"
 * On success it BRIDGES the session into Redux — that's what feeds
 * selectIsAuthenticated / selectHasPaid, which the paid/unpaid guards
 * (layer 2) read. Without this dispatch, the guards spin forever.
 */
// const ProtectedRoutes = () => {
//   const dispatch = useAppDispatch();
//   const { error, isError, isSuccess, data: sessionResponse } = useAuth();
//   console.log("sessionResponse", sessionResponse);

//   // Sync session → store. Effects, never render: render must stay pure.
//   useEffect(() => {
//     if (isSuccess && sessionResponse?.data) {
//       // adjust `.user` to your actual response shape
//       dispatch(setUser(sessionResponse?.data));
//     }
//   }, [isSuccess, sessionResponse, dispatch]);

//   useEffect(() => {
//     if (isError) {
//       dispatch(clearUser());
//       toastService.error(
//         "Session expired",
//         (error?.message as string) ?? "Please log in again",
//       );
//     }
//   }, [isError, error, dispatch]);

//   if (isError) {
//     // Absolute path + replace. Relative "login" resolves against the
//     // CURRENT url — from /payment/methods/credit-card it would go to
//     // /payment/methods/login. replace keeps the dead page out of history.
//     return <Navigate to="/login" replace />;
//   }

//   if (!isSuccess) {
//     // Pending — previously this branch returned undefined (blank screen)
//     return (
//       <div
//         style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}
//       >
//         <Spinner size="md" />
//       </div>
//     );
//   }

//   return <Outlet />;
// };
// export default ProtectedRoutes;
